import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/axios';

const API_BASE = process.env.REACT_APP_API_UPLOAD_URL;

const DEFAULT_BRANDING = {
  businessName: '',
  tagline: '',
  primaryColor: '#2563eb',
  secondaryColor: '#1d4ed8',
  backgroundColor: '#f8fafc',
  textColor: '#0f172a',
  buttonTextColor: '#ffffff',
};

const ColorField = ({ label, value, onChange, hint }) => (
  <div className="form-group">
    <label>{label}</label>
    {hint && <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, marginTop: -2 }}>{hint}</p>}
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: 44, height: 44, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2, background: 'none' }}
      />
      <input
        className="form-control"
        style={{ flex: 1 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="#2563eb"
        maxLength={7}
      />
    </div>
  </div>
);

export default function BrandingSettings() {
  const [form, setForm] = useState(DEFAULT_BRANDING);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogo, setExistingLogo] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    api.get('/branding')
      .then(r => {
        if (r.data.data) {
          const b = r.data.data;
          setForm({
            businessName: b.businessName || '',
            tagline: b.tagline || '',
            primaryColor: b.primaryColor || '#2563eb',
            secondaryColor: b.secondaryColor || '#1d4ed8',
            backgroundColor: b.backgroundColor || '#f8fafc',
            textColor: b.textColor || '#0f172a',
            buttonTextColor: b.buttonTextColor || '#ffffff',
          });
          if (b.logoUrl) setExistingLogo(`${API_BASE}${b.logoUrl}`);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogo(null);
    setRemoveLogo(true);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      if (logoFile) formData.append('logo', logoFile);
      if (removeLogo) formData.append('removeLogo', 'true');

      await api.put('/branding', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Branding saved successfully!');
      setLogoFile(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save branding');
    } finally { setSaving(false); }
  };

  const setColor = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  // Live preview values
  const previewGradient = `linear-gradient(135deg, ${form.primaryColor}, ${form.secondaryColor})`;
  const displayLogo = logoPreview || (!removeLogo && existingLogo) || null;

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="page-header">
        <h3>Brand Settings</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Customize how your brand appears on all customer-facing feedback pages.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className={'branding-form'} style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }}>

        {/* ── Settings form ── */}
        <form onSubmit={handleSave}>

          {/* Identity */}
          <div className="card mb-3">
            <h4 style={{ marginBottom: 18, fontSize: 15 }}>Identity</h4>

            <div className="form-group">
              <label>Business Name</label>
              <input className="form-control" value={form.businessName} onChange={e => setForm({ ...form, businessName: e.target.value })} placeholder="Your Business Name" />
            </div>

            <div className="form-group">
              <label>Tagline <span style={{ color: 'var(--text-muted)', textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-control" value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} placeholder="e.g. Your feedback makes us better" />
            </div>

            {/* Logo upload */}
            <div className="form-group">
              <label>Logo</label>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, marginTop: -2 }}>PNG, JPG or SVG · Max 5MB · Recommended: 300×100px</p>

              {displayLogo ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--bg)', borderRadius: 10, padding: '12px 16px', border: '1px solid var(--border)', marginBottom: 10 }}>
                  <img src={displayLogo} alt="logo preview" style={{ height: 52, maxWidth: 160, objectFit: 'contain', borderRadius: 6 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, fontWeight: 600 }}>Logo uploaded</p>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>Looks great on the feedback page</p>
                  </div>
                  <button type="button" className="btn btn-danger btn-sm" onClick={handleRemoveLogo}>Remove</button>
                </div>
              ) : (
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: '24px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg)', transition: 'all .2s' }}
                  onMouseOver={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ fontSize: 28, marginBottom: 6 }}>🖼️</div>
                  <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Click to upload logo</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>PNG, JPG or SVG</p>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
            </div>
          </div>

          {/* Colors */}
          <div className="card mb-3">
            <h4 style={{ marginBottom: 4, fontSize: 15 }}>Colors</h4>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>These colors are applied to the feedback header, buttons, and background.</p>

            <div className="grid-2">
              <ColorField label="Primary Color" value={form.primaryColor} onChange={setColor('primaryColor')} hint="Header gradient start · Button color" />
              <ColorField label="Secondary Color" value={form.secondaryColor} onChange={setColor('secondaryColor')} hint="Header gradient end" />
              <ColorField label="Background Color" value={form.backgroundColor} onChange={setColor('backgroundColor')} hint="Page background" />
              <ColorField label="Text Color" value={form.textColor} onChange={setColor('textColor')} hint="Body text on page" />
              <ColorField label="Button Text Color" value={form.buttonTextColor} onChange={setColor('buttonTextColor')} hint="Text on header and buttons" />
            </div>
          </div>

          <button className="btn btn-primary btn-lg" type="submit" disabled={saving} style={{ width: '100%', justifyContent: 'center' }}>
            {saving ? 'Saving…' : '💾 Save Branding'}
          </button>
        </form>

        {/* ── Live Preview ── */}
        <div style={{ position: 'sticky', top: 24 }}>
          <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)', marginBottom: 12 }}>
            Live Preview — Customer Feedback Page
          </p>
          <div style={{ background: form.backgroundColor, borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,.12)', border: '1px solid var(--border)' }}>

            {/* Branded header preview */}
            <div style={{ background: previewGradient, padding: '24px 20px', textAlign: 'center' }}>
              {displayLogo ? (
                <img src={displayLogo} alt="logo" style={{ height: 48, maxWidth: 150, objectFit: 'contain', borderRadius: 8, marginBottom: 10, background: 'rgba(255,255,255,.15)', padding: '4px 10px' }} />
              ) : (
                <div style={{ fontSize: 36, marginBottom: 8 }}>💬</div>
              )}
              <div style={{ color: form.buttonTextColor, fontWeight: 700, fontSize: 18 }}>{form.businessName || 'Your Business'}</div>
              {form.tagline && <div style={{ color: form.buttonTextColor, opacity: .8, fontSize: 12, marginTop: 4 }}>{form.tagline}</div>}
              <div style={{ color: form.buttonTextColor, opacity: .7, fontSize: 12, marginTop: 8 }}>👋 Hi, John! · 🧾 Bill: INV-001</div>
            </div>

            {/* Sample question preview */}
            <div style={{ padding: '16px', color: form.textColor }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: '14px', marginBottom: 12, border: '1px solid var(--border)' }}>
                <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: form.textColor }}>
                  <span style={{ color: form.primaryColor, marginRight: 6 }}>Q1.</span>How was the service today?
                </p>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                  {['😠','😢','😐','😊','🤩'].map((e, i) => (
                    <div key={i} style={{
                      flex: 1, textAlign: 'center', padding: '8px 4px', borderRadius: 8,
                      border: `2px solid ${i === 3 ? form.primaryColor : 'var(--border)'}`,
                      background: i === 3 ? form.primaryColor + '18' : '#fff',
                      fontSize: 18,
                    }}>
                      {e}
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit button preview */}
              <div style={{
                background: form.primaryColor,
                color: form.buttonTextColor,
                borderRadius: 10,
                padding: '11px',
                textAlign: 'center',
                fontWeight: 700,
                fontSize: 14,
              }}>
                ✅ Submit Feedback
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
